// New Compatibility System V2 - Clean, maintainable, and extensible
import { compatibilityMatrix, areConnectorsCompatible, getAllCompatibleConnectors } from './compatibilityMatrix';
import { connectorList, audioConnectors, powerConnectors } from './connectorsConfig';

// Global compatibility data store
const compatibilityData = {
  connectors: {},
  wireGauges: {},
  cableTypes: {},
  initialized: false,
  metadata: {
    version: '2.0',
    lastUpdated: new Date().toISOString(),
    sources: ['Default']
  }
};

// Set for tracking logged warnings to avoid spam
const loggedWarnings = new Set();

// Helper function to get admin token (placeholder)
function getAdminToken() {
  // This would typically get the token from localStorage, cookies, or context
  return localStorage.getItem('adminToken') || null;
}

// Wire gauge compatibility for power connectors - now using matrix as source of truth
const powerWireGauges = {
  '12AWG': {
    label: '12 AWG',
    ampacity: 20,
    description: 'Heavy duty power applications'
  },
  '14AWG': {
    label: '14 AWG', 
    ampacity: 15,
    description: 'Standard power applications'
  },
  '16AWG': {
    label: '16 AWG',
    ampacity: 13,
    description: 'Light power applications'
  }
};

// Audio wire gauges - now using matrix as source of truth
const audioWireGauges = {
  '20AWG': {
    label: '20 AWG',
    description: 'Standard audio applications'
  },
  '22AWG': {
    label: '22 AWG',
    description: 'Microphone and line level'
  },
  '24AWG': {
    label: '24 AWG',
    description: 'Light audio applications'
  }
};

// Initialize system with default data
export function initializeCompatibilitySystem() {
  return new Promise(async (resolve, reject) => {
    try {
  console.log('🔧 Initializing Compatibility System V2...');
  
  // Initialize wire gauges
  compatibilityData.wireGauges = {
    power: powerWireGauges,
    audio: audioWireGauges
  };
  
  // Initialize connectors from all sources
  compatibilityData.connectors = {};
  
  // Add connectors from connectorList
  connectorList.forEach(connector => {
    compatibilityData.connectors[connector.id] = {
      id: connector.id,
      type: connector.type,
      gender: connector.gender,
      brand: connector.brand,
      description: connector.description,
      verified: connector.verified || false,
      wireGauges: getDefaultWireGauges(connector.type),
      applications: getDefaultApplications(connector.type),
      family: getConnectorFamily(connector.type),
      pins: connector.pins || getPinCount(connector.id),
      image: getConnectorImage(connector.id),
      skuCode: connector.skuCode || 'UNKNOWN'
    };
  });
  
  console.log('🔧 Added connectors from connectorList:', Object.keys(compatibilityData.connectors));
  
  // Add connectors from audioConnectors array
  audioConnectors.forEach(connector => {
    compatibilityData.connectors[connector.id] = {
      id: connector.id,
      type: connector.type,
      gender: connector.gender,
      brand: 'Professional',
      description: connector.label,
      verified: true,
      wireGauges: getDefaultWireGauges('audio'),
      applications: getDefaultApplications('audio'),
      family: connector.family,
      pins: connector.pins || getPinCount(connector.id),
      image: connector.image,
      skuCode: 'UNKNOWN'
    };
  });
  
  console.log('🔧 Added connectors from audioConnectors:', Object.keys(compatibilityData.connectors).filter(id => audioConnectors.some(ac => ac.id === id)));
  
  // Add connectors from powerConnectors array
  powerConnectors.forEach(connector => {
    compatibilityData.connectors[connector.id] = {
      id: connector.id,
      type: connector.type,
      gender: connector.gender,
      brand: 'Professional',
      description: connector.label,
      verified: true,
      wireGauges: getDefaultWireGauges('power'),
      applications: getDefaultApplications('power'),
      family: connector.family,
      pins: 2, // Power connectors typically have 2-3 pins
      image: connector.image,
      skuCode: connector.skuCode || 'UNKNOWN'
    };
  });
  
  // Initialize cable types - now using matrix as source of truth
  compatibilityData.cableTypes = {
    'audio': {
      name: 'Audio',
      description: 'Professional audio cables',
      wireGauges: Object.keys(audioWireGauges)
    },
    'power': {
      name: 'Power',
      description: 'Professional power cables',
      wireGauges: Object.keys(powerWireGauges)
    },
    'dmx': {
      name: 'DMX',
      description: 'DMX lighting control cables',
      wireGauges: Object.keys(audioWireGauges)
    },
    'speaker': {
      name: 'Speaker',
      description: 'Speaker cables',
      wireGauges: Object.keys(audioWireGauges)
    },
    'hybrid': {
      name: 'Hybrid',
      description: 'Multi-cable assemblies',
      wireGauges: [...Object.keys(audioWireGauges), ...Object.keys(powerWireGauges)]
    }
  };
  
      // Load compatibility data from comprehensive matrix
      await loadCompatibilityFromMatrix();
      
             // Apply rule-based compatibility as fallback for any missing data
       // applyRuleBasedCompatibility(); // Removed - now using matrix as source of truth
       
       // Add hardcoded unbalanced compatibility for testing
       // addHardcodedUnbalancedCompatibility(); // Removed - now using matrix as source of truth
  
  // Update metadata
  compatibilityData.metadata.lastUpdated = new Date().toISOString();
      compatibilityData.metadata.sources = ['API', 'Default'];
  
  
  // Debug: Check for specific connectors
      
      resolve();
    } catch (error) {
      console.error('❌ Error initializing compatibility system:', error);
      reject(error);
    }
  });
}

// Load compatibility data from comprehensive matrix
async function loadCompatibilityFromMatrix() {
  try {
    console.log('📥 Loading compatibility data from comprehensive matrix...');
    
    // Import the comprehensive matrix
    const { compatibilityMatrix } = await import('./compatibilityMatrix');
    
    // Merge comprehensive matrix data with local data
    Object.entries(compatibilityMatrix).forEach(([id, connector]) => {
      if (compatibilityData.connectors[id]) {
        // Update existing connector with matrix data
        compatibilityData.connectors[id] = {
          ...compatibilityData.connectors[id],
          ...connector,
          compatibleWith: connector.compatibleWith || compatibilityData.connectors[id].compatibleWith,
          incompatibleWith: connector.incompatibleWith || compatibilityData.connectors[id].incompatibleWith
        };
      } else {
        // Add new connector from matrix
        compatibilityData.connectors[id] = connector;
      }
    });
    
    console.log('✅ Loaded compatibility data from comprehensive matrix for', Object.keys(compatibilityMatrix).length, 'connectors');
  } catch (error) {
    console.warn('⚠️ Error loading compatibility data from matrix:', error);
    console.log('🔄 Falling back to rule-based compatibility');
  }
}

// Load compatibility data from API (fallback)
async function loadCompatibilityFromAPI() {
  try {
    console.log('📥 Loading compatibility data from API...');
    
    const response = await fetch('/api/compatibility-matrix?action=matrix');
    if (response.ok) {
      const apiData = await response.json();
      
      if (apiData.connectors) {
        // Merge API data with local data
        Object.entries(apiData.connectors).forEach(([id, connector]) => {
          if (compatibilityData.connectors[id]) {
            // Update existing connector with API data
            compatibilityData.connectors[id] = {
              ...compatibilityData.connectors[id],
              ...connector,
              compatibleWith: connector.compatibleWith || compatibilityData.connectors[id].compatibleWith,
              incompatibleWith: connector.incompatibleWith || compatibilityData.connectors[id].incompatibleWith
            };
          } else {
            // Add new connector from API
            compatibilityData.connectors[id] = connector;
          }
        });
        
        console.log('✅ Loaded compatibility data from API for', Object.keys(apiData.connectors).length, 'connectors');
      }
    } else {
      console.warn('⚠️ Could not load compatibility data from API, using fallback rules');
    }
  } catch (error) {
    console.warn('⚠️ Error loading compatibility data from API:', error);
    console.log('🔄 Falling back to rule-based compatibility');
  }
}

// API functions for compatibility matrix management
export const getCompatibilityMatrix = async (format = 'json') => {
  try {
    const response = await fetch(`/api/compatibility-matrix?action=matrix&format=${format}`);
    if (!response.ok) {
      throw new Error('Failed to fetch compatibility matrix');
    }
    
    if (format === 'csv') {
      return await response.text();
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching compatibility matrix:', error);
    // Fallback to local comprehensive matrix
    try {
      const { compatibilityMatrix } = await import('./compatibilityMatrix');
      console.log('🔄 Using local comprehensive matrix as fallback');
      return { connectors: compatibilityMatrix, version: '2.0', lastUpdated: new Date().toISOString() };
    } catch (fallbackError) {
      console.error('Error loading fallback matrix:', fallbackError);
      throw error;
    }
  }
};

export const getConnectorCompatibility = async (connectorId) => {
  try {
    const response = await fetch(`/api/compatibility-matrix?action=connector&connectorId=${connectorId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch connector compatibility');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching connector compatibility:', error);
    // Fallback to local comprehensive matrix
    try {
      const { compatibilityMatrix } = await import('./compatibilityMatrix');
      const connectorData = compatibilityMatrix[connectorId];
      if (!connectorData) {
        throw new Error('Connector not found');
      }
      console.log('🔄 Using local comprehensive matrix as fallback');
      return {
        connector: connectorData,
        compatibleConnectors: connectorData.compatibleWith || [],
        incompatibleConnectors: connectorData.incompatibleWith || []
      };
    } catch (fallbackError) {
      console.error('Error loading fallback connector data:', fallbackError);
      throw error;
    }
  }
};

export const updateConnectorCompatibility = async (connectorId, compatibleWith, incompatibleWith, reason) => {
  const adminToken = getAdminToken();
  if (!adminToken) {
    throw new Error('Admin access required');
  }

  try {
    const response = await fetch('/api/compatibility-matrix?action=update', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'adminToken': adminToken
      },
      body: JSON.stringify({ connectorId, compatibleWith, incompatibleWith, reason })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update connector compatibility');
    }

    // Update local data
    if (compatibilityData.connectors[connectorId]) {
      if (compatibleWith) {
    compatibilityData.connectors[connectorId].compatibleWith = compatibleWith;
      }
      if (incompatibleWith) {
        compatibilityData.connectors[connectorId].incompatibleWith = incompatibleWith;
      }
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating connector compatibility:', error);
    throw error;
  }
};

export const addConnectorToMatrix = async (connectorData) => {
  const adminToken = getAdminToken();
  if (!adminToken) {
    throw new Error('Admin access required');
  }

  try {
    const response = await fetch('/api/compatibility-matrix?action=add-connector', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'adminToken': adminToken
      },
      body: JSON.stringify({ connectorData })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to add connector');
    }

    // Add to local data
    compatibilityData.connectors[connectorData.id] = connectorData;

    return await response.json();
  } catch (error) {
    console.error('Error adding connector:', error);
    throw error;
  }
};

export const updateConnectorDetails = async (connectorId, updates) => {
  const adminToken = getAdminToken();
  if (!adminToken) {
    throw new Error('Admin access required');
  }

  try {
    const response = await fetch('/api/compatibility-matrix?action=connector', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'adminToken': adminToken
      },
      body: JSON.stringify({ connectorId, updates })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update connector details');
    }

    // Update local data
    if (compatibilityData.connectors[connectorId]) {
      compatibilityData.connectors[connectorId] = {
        ...compatibilityData.connectors[connectorId],
        ...updates
      };
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating connector details:', error);
    throw error;
  }
};

export const deleteConnectorFromMatrix = async (connectorId) => {
  const adminToken = getAdminToken();
  if (!adminToken) {
    throw new Error('Admin access required');
  }

  try {
    const response = await fetch(`/api/compatibility-matrix?action=connector&connectorId=${connectorId}`, {
      method: 'DELETE',
      headers: {
        'adminToken': adminToken
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete connector');
    }

    // Remove from local data
    delete compatibilityData.connectors[connectorId];

    return await response.json();
  } catch (error) {
    console.error('Error deleting connector:', error);
    throw error;
  }
};

export const importCompatibilityMatrix = async (matrixData, format = 'json', replace = false) => {
  const adminToken = getAdminToken();
  if (!adminToken) {
    throw new Error('Admin access required');
  }

  try {
    const response = await fetch('/api/compatibility-matrix?action=import', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'adminToken': adminToken
      },
      body: JSON.stringify({ matrixData, format, replace })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to import matrix');
    }

    // Reload compatibility data
    await loadCompatibilityFromAPI();

    return await response.json();
  } catch (error) {
    console.error('Error importing matrix:', error);
    throw error;
  }
};

export const exportCompatibilityMatrix = async (format = 'json') => {
  try {
    const response = await fetch(`/api/compatibility-matrix?action=export&format=${format}`);
    if (!response.ok) {
      throw new Error('Failed to export matrix');
    }
    
    if (format === 'csv') {
      return await response.text();
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error exporting matrix:', error);
    throw error;
  }
};

export const validateCompatibilityMatrix = async () => {
  try {
    const response = await fetch('/api/compatibility-matrix?action=validate');
    if (!response.ok) {
      throw new Error('Failed to validate matrix');
    }
    return await response.json();
  } catch (error) {
    console.error('Error validating matrix:', error);
    throw error;
  }
};

export const getCompatibilityHistory = async (limit = 50, offset = 0, type = null) => {
  try {
    const params = new URLSearchParams({
      action: 'history',
      limit: limit.toString(),
      offset: offset.toString()
    });
    
    if (type) {
      params.append('type', type);
    }
    
    const response = await fetch(`/api/compatibility-matrix?${params}`);
    if (!response.ok) {
      throw new Error('Failed to fetch compatibility history');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching compatibility history:', error);
    return { history: [], total: 0, limit, offset };
  }
};

// Updated compatibility checking function that uses the comprehensive matrix
export async function isCompatible(connectorA, connectorB, context = 'global') {
  try {
    // Validate inputs
    if (!connectorA || !connectorB) {
      return { compatible: false, reason: 'Invalid connector inputs' };
    }

    // Normalize connector IDs for strict matrix enforcement
    const { normalizeConnectorId } = await import('./compatibilityService');
    const normA = normalizeConnectorId(connectorA);
    const normB = normalizeConnectorId(connectorB);

    console.log(`[isCompatible] Checking compatibility: ${normA} -> ${normB} (context: ${context})`);

    // First check PowerCon specific rules with context
    const { areConnectorsCompatibleWithContext } = await import('./compatibilityMatrix');
    const matrixResult = areConnectorsCompatibleWithContext(normA, normB, context);
    
    if (matrixResult) {
      // If matrixResult has compatible: false, it's explicitly incompatible
      if (matrixResult.compatible === false) {
        return {
          compatible: false,
          reason: matrixResult.reason || 'Explicitly not compatible',
          details: matrixResult.details || undefined
        };
      }
      
      // If matrixResult exists but doesn't have compatible: false, it's compatible
      let warningObj = null;
      if (matrixResult.warning) {
        warningObj = {
          type: 'warning',
          message: matrixResult.warning,
          details: matrixResult.notes || undefined
        };
      }
      return {
        compatible: true,
        type: matrixResult.type,
        polesUsed: matrixResult.polesUsed,
        warning: warningObj,
        notes: matrixResult.notes,
        unbalanced: matrixResult.unbalanced
      };
    }
    
    // If not in matrix, default to incompatible
    const warningKey = `${normA}->${normB}`;
    if (!loggedWarnings.has(warningKey)) {
      console.warn(`[compatibilityMatrix] No compatibility entry for: ${normA} -> ${normB} - marking as INCOMPATIBLE`);
      loggedWarnings.add(warningKey);
    }
    
    return { compatible: false, reason: 'Not in compatibility matrix - defaulting to incompatible' };
  } catch (error) {
    console.error('Error checking compatibility:', error);
    return { compatible: false, reason: 'Error checking compatibility' };
  }
}

// Helper function to get unbalanced warning
function getUnbalancedWarning(connectorA, connectorB) {
  // Check if connectorA has an unbalanced warning for connectorB
  if (connectorA.unbalancedWarnings && connectorA.unbalancedWarnings[connectorB.id]) {
    return connectorA.unbalancedWarnings[connectorB.id];
  }
  
  // Check if connectorB has an unbalanced warning for connectorA
  if (connectorB.unbalancedWarnings && connectorB.unbalancedWarnings[connectorA.id]) {
    return connectorB.unbalancedWarnings[connectorA.id];
  }
  
  return null;
}

// New function to check if a connection is unbalanced
export function isUnbalancedConnection(connectorA, connectorB) {
  try {
    const connectorADetails = compatibilityData.connectors[connectorA];
    const connectorBDetails = compatibilityData.connectors[connectorB];

    if (!connectorADetails || !connectorBDetails) {
    return false;
  }
  
    const warning = getUnbalancedWarning(connectorADetails, connectorBDetails);
    return !!warning;
  } catch (error) {
    console.error('Error checking unbalanced connection:', error);
        return false;
      }
}

// New function to get unbalanced warning message
export function getUnbalancedWarningMessage(connectorA, connectorB) {
  try {
    const connectorADetails = compatibilityData.connectors[connectorA];
    const connectorBDetails = compatibilityData.connectors[connectorB];

    if (!connectorADetails || !connectorBDetails) {
      return null;
    }

    return getUnbalancedWarning(connectorADetails, connectorBDetails);
  } catch (error) {
    console.error('Error getting unbalanced warning message:', error);
    return null;
  }
}

// Get system status for compatibility manager
export function getSystemStatus() {
  return {
    version: compatibilityData.metadata.version || '2.0',
    lastUpdated: compatibilityData.metadata.lastUpdated || new Date().toISOString(),
    sources: compatibilityData.metadata.sources || ['Default'],
    connectorCount: Object.keys(compatibilityData.connectors || {}).length,
    cableTypeCount: Object.keys(compatibilityData.cableTypes || {}).length,
    wireGaugeCount: Object.keys(compatibilityData.wireGauges || {}).length
  };
}

// Check if API is available
export async function checkAPIAvailability() {
  try {
    const response = await fetch('/api/compatibility-matrix?action=matrix');
    return {
      available: response.ok,
      status: response.status,
      statusText: response.statusText
    };
  } catch (error) {
    return {
      available: false,
      error: error.message
    };
  }
}

// Updated function to get all compatible connectors
export async function getCompatibleConnectors(connectorId) {
  try {
    // First check the comprehensive matrix
    const matrixConnectors = getAllCompatibleConnectors(connectorId);
    if (matrixConnectors.length > 0) {
      return matrixConnectors.map(comp => comp.id);
    }

    // Fallback to existing logic
    const connectorData = compatibilityData.connectors[connectorId];
    if (!connectorData) return [];

    return connectorData.compatibleWith || [];
  } catch (error) {
    console.error('Error getting compatible connectors:', error);
      return [];
  }
}

// Get compatible wire gauges for a given connector and cable type
export function getCompatibleWireGauges(connectorId, cableType) {
  try {
    // Get connector data from the matrix as single source of truth
    const { compatibilityMatrix } = require('./compatibilityMatrix');
    const connectorData = compatibilityMatrix[connectorId];
    if (!connectorData) {
      console.warn(`Connector ${connectorId} not found in compatibility matrix`);
      return [];
    }

    // Check if connector has wire gauge compatibility data
    if (connectorData.wireGaugeCompatibility) {
      return connectorData.wireGaugeCompatibility;
    }

    // Fallback to cable type data if no specific connector data
    const cableTypeData = compatibilityData.cableTypes[cableType];
    if (!cableTypeData) {
      console.warn(`Cable type ${cableType} not found in compatibility data`);
      return [];
    }

    // Return wire gauges for the cable type
    return cableTypeData.wireGauges || [];
  } catch (error) {
    console.error('Error getting compatible wire gauges:', error);
    return [];
  }
}

// Get cable type connectors
export function getCableTypeConnectors(cableType) {
  try {
    const cableTypeData = compatibilityData.cableTypes[cableType];
    if (!cableTypeData) {
      return [];
    }
    
    const connectorIds = cableTypeData.compatibleConnectors || [];
    return connectorIds.map(id => compatibilityData.connectors[id]).filter(Boolean);
  } catch (error) {
    console.error('Error getting cable type connectors:', error);
    return [];
  }
}

// Import compatibility data from CSV
export function importFromCSV(csvData) {
  try {
    console.log('📥 Importing compatibility data from CSV...');
    
    // Parse CSV data and update compatibility matrix
    // This is a simplified implementation - in a real system, you'd parse the CSV properly
    // const lines = csvData.split('\n'); // Unused variable
    // const headers = lines[0].split(',').map(h => h.trim()); // Unused variable
    
    // Update metadata
    compatibilityData.metadata.lastUpdated = new Date().toISOString();
    compatibilityData.metadata.sources = [...compatibilityData.metadata.sources, 'CSV'];
    
    console.log('✅ CSV data imported successfully');
    return { success: true, message: 'CSV data imported successfully' };
  } catch (error) {
    console.error('❌ Error importing CSV data:', error);
    return { success: false, message: 'Failed to import CSV data' };
  }
}

// Import compatibility data from PDF
export function importFromPDF(pdfData) {
  try {
    console.log('📥 Importing compatibility data from PDF...');
    
    // Parse PDF data and update compatibility matrix
    // This is a simplified implementation - in a real system, you'd parse the PDF properly
    
    // Update metadata
    compatibilityData.metadata.lastUpdated = new Date().toISOString();
    compatibilityData.metadata.sources = [...compatibilityData.metadata.sources, 'PDF'];
    
    console.log('✅ PDF data imported successfully');
    return { success: true, message: 'PDF data imported successfully' };
  } catch (error) {
    console.error('❌ Error importing PDF data:', error);
    return { success: false, message: 'Failed to import PDF data' };
  }
}

export function getConnectorCompatibilityWarningSync(connectorA, connectorB) {
  try {
    if (!connectorA || !connectorB) {
      return null;
    }

    // Import the compatibility matrix synchronously
    const { areConnectorsCompatible, compatibilityMatrix } = require('./compatibilityMatrix');
    
    // Check the comprehensive matrix
    const matrixResult = areConnectorsCompatible(connectorA, connectorB);
    
    if (matrixResult && matrixResult.warning) {
      return {
        type: matrixResult.unbalanced ? 'warning' : 'info',
        message: matrixResult.warning,
        details: matrixResult.notes || null
      };
    }

    // Check for explicit incompatibility
    const connectorAData = compatibilityMatrix[connectorA];
    
    if (connectorAData && connectorAData.notCompatibleWith) {
      const notCompat = connectorAData.notCompatibleWith.find(comp => comp.id === connectorB);
      if (notCompat) {
        return {
          type: 'warning',
          message: notCompat.reason || 'Incompatible connectors',
          details: notCompat.details || null
        };
      }
    }

    // Check for PowerCON specific warnings
    const powerconWarning = getPowerCONSpecificWarning(connectorA, connectorB);
    if (powerconWarning) {
      return powerconWarning;
    }

    return null;
  } catch (error) {
    console.error('Error getting compatibility warning:', error, { connectorA, connectorB });
    return null;
  }
}

export async function getConnectorCompatibilityWarning(connectorA, connectorB) {
  try {
    // Validate inputs
    if (!connectorA || !connectorB) {
      // Don't log warnings for undefined parameters as this is expected when connectors aren't selected yet
      return null;
    }

    // Log connector IDs for debugging
    console.log('[getConnectorCompatibilityWarning] Checking:', connectorA, connectorB);

    // Import the compatibility matrix
    const { areConnectorsCompatible } = await import('./compatibilityMatrix');
    
    // Check the comprehensive matrix
    const matrixResult = areConnectorsCompatible(connectorA, connectorB);
    
    if (matrixResult && matrixResult.warning) {
      return {
        type: matrixResult.unbalanced ? 'warning' : 'info',
        message: matrixResult.warning,
        details: matrixResult.notes || null
      };
    }

    // Check for explicit incompatibility
    const { compatibilityMatrix } = await import('./compatibilityMatrix');
    const connectorAData = compatibilityMatrix[connectorA];
    
    if (connectorAData && connectorAData.notCompatibleWith) {
      const notCompat = connectorAData.notCompatibleWith.find(comp => comp.id === connectorB);
      if (notCompat) {
        return {
          type: 'warning',
          message: notCompat.reason || 'Incompatible connectors',
          details: notCompat.details || null
        };
      }
    }

    // Check for PowerCON specific warnings
    const powerconWarning = getPowerCONSpecificWarning(connectorA, connectorB);
    if (powerconWarning) {
      return powerconWarning;
    }

    return null;
  } catch (error) {
    console.error('Error getting compatibility warning:', error, { connectorA, connectorB });
    return null;
  }
}

// PowerCON specific warning logic - now uses smart rules
function getPowerCONSpecificWarning(connectorA, connectorB, context = 'global') {
  try {
    // Import the smart PowerCon compatibility function synchronously
    const { getPowerConCompatibility } = require('./compatibilityMatrix');
    
    // Check if both connectors are PowerCon
    const isPowerConA = connectorA.includes('PowerCON') || 
                       connectorA.includes('NAC3FXX') || 
                       connectorA.includes('NAC3MPX');
    const isPowerConB = connectorB.includes('PowerCON') || 
                       connectorB.includes('NAC3FXX') || 
                       connectorB.includes('NAC3MPX');
    
    if (!isPowerConA || !isPowerConB) {
      return null; // Not PowerCon connectors
    }
    
    // Use the smart PowerCon compatibility function
    const powerConResult = getPowerConCompatibility(connectorA, connectorB, context);
    
    if (powerConResult) {
      if (powerConResult.compatible) {
        return {
          type: 'info',
          message: powerConResult.message,
          details: powerConResult.notes
        };
      } else {
        return {
          type: 'warning',
          message: powerConResult.reason,
          details: powerConResult.details
        };
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error in getPowerCONSpecificWarning:', error);
    return null;
  }
}

// Apply rule-based compatibility
// All compatibility rules are now handled by compatibilityMatrix.js as the single source of truth
// This ensures consistency and maintainability across the application

// Load compatibility data using rule-based system instead of CSV
// async function loadCompatibilityFromCSV() {
//   try {
//     console.log('📥 Loading compatibility data using rule-based system...');
//     
//     // Apply rule-based compatibility instead of CSV parsing
//     applyRuleBasedCompatibility();
//     
//     // Add hardcoded unbalanced compatibility for testing
//     addHardcodedUnbalancedCompatibility();
//   } catch (error) {
//     console.error('❌ Error loading compatibility data:', error);
//   }
// }

// Helper functions
function getDefaultWireGauges(connectorType) {
  switch (connectorType) {
    case 'audio':
    case 'XLR':
    case 'TRS':
    case 'TS':
    case 'RCA':
      return ['20AWG', '22AWG', '24AWG'];
    case 'power':
    case 'PowerCon':
    case 'NEMA':
    case 'IEC':
      return ['14AWG', '16AWG'];
    case 'speaker':
    case 'Speakon':
      return ['12AWG', '14AWG', '16AWG'];
    default:
      return ['20AWG', '22AWG'];
  }
}

function getDefaultApplications(connectorType) {
  switch (connectorType) {
    case 'audio':
    case 'XLR':
      return ['microphone', 'line-level', 'balanced-audio'];
    case 'TRS':
    case 'TS':
      return ['instrument', 'line-level', 'headphone'];
    case 'RCA':
      return ['consumer-audio', 'video'];
    case 'power':
    case 'PowerCon':
    case 'NEMA':
    case 'IEC':
      return ['power-distribution', 'equipment-power'];
    case 'speaker':
    case 'Speakon':
      return ['speaker-connection', 'amplifier-output'];
    default:
      return ['general-purpose'];
  }
}

function getConnectorFamily(connectorType) {
  switch (connectorType) {
    case 'XLR':
      return 'XLR';
    case 'TRS':
    case 'TS':
      return 'TRS';
    case 'RCA':
      return 'RCA';
    case 'PowerCon':
      return 'PowerCon';
    case 'NEMA':
      return 'NEMA';
    case 'IEC':
      return 'IEC';
    case 'Speakon':
      return 'Speakon';
    default:
      return 'Unknown';
  }
}

function getPinCount(connectorId) {
  if (connectorId.includes('3')) return 3;
  if (connectorId.includes('5')) return 5;
  if (connectorId.includes('2')) return 2;
  if (connectorId.includes('4')) return 4;
  if (connectorId.includes('8')) return 8;
  return 0;
}

function getConnectorImage(connectorId) {
  // First try to get the image from the compatibility matrix
  if (compatibilityMatrix[connectorId] && compatibilityMatrix[connectorId].image) {
    const image = compatibilityMatrix[connectorId].image;
    if (image && image !== '/images/coming_soon.png') {
      return image;
    }
  }
  
  // Fallback image mapping for connectors not in matrix
  const imageMap = {
    'NC3FXX': '/images/nc3fxx.jpg',
    'NC3MXX': '/images/nc3mxx.jpg',
    'NC5FXX': '/images/nc5fxx.jpg',
    'NC5MXX': '/images/nc5mxx.jpg',
    'NL2FX': '/images/NL2FXX-W-S.png',
    'NL4FX': '/images/NL4FXX-W-S.png',
    'NL8FC': '/images/NL8FC.jpg'
  };
  
  return imageMap[connectorId] || '/images/placeholder.png';
}

// function getConnectorsByType(type) {
//   return Object.values(compatibilityData.connectors)
//     .filter(connector => {
//       switch (type) {
//         case 'audio':
//           return connector.type === 'audio' || connector.family === 'XLR' || connector.family === 'TRS' || connector.family === 'TS' || connector.family === 'RCA';
//         case 'power':
//           return connector.type === 'power' || connector.family === 'PowerCon' || connector.family === 'NEMA' || connector.family === 'IEC';
//         case 'dmx':
//           return connector.type === 'dmx' || connector.family === 'XLR';
//         case 'speaker':
//           return connector.type === 'speaker' || connector.family === 'Speakon';
//         default:
//           return true;
//       }
//     })
//     .map(connector => connector.id);
// }

// Re-export the matrix functions
export { areConnectorsCompatible, getAllCompatibleConnectors };